import { GAP_X, LEVEL_HEIGHT, NODE_WIDTH } from "./treeGraph.constants";

/** One step of the hierarchical grid: a column of the generation row, and a row of the tree. */
const STEP_X = NODE_WIDTH + GAP_X;
const STEP_Y = LEVEL_HEIGHT;

/**
 * How far out the search walks before it stops looking. Twelve rings is a clear square either side
 * of where the node wants to sit — far more room than one ever needs — and the bound is what stops a
 * pathological tree (every free spot taken for thousands of pixels) from searching forever.
 */
const MAX_RINGS = 12;

type Candidate = { dx: number; dy: number; x: number; y: number };

/**
 * Reading order within a ring: the nearest row first, the row below before the row above (that is
 * the way the generations run, and the empty canvas is down there rather than up among the floating
 * organization hubs), then the right-hand side before the left, nearest column first. It is the
 * order someone scanning outwards from what they were just working on would follow, so the new node
 * turns up where they are already looking.
 */
const byReadingOrder = (a: Candidate, b: Candidate): number =>
	Math.abs(a.dy) - Math.abs(b.dy) ||
	Number(a.dy < 0) - Number(b.dy < 0) ||
	Number(a.dx < 0) - Number(b.dx < 0) ||
	Math.abs(a.dx) - Math.abs(b.dx);

/**
 * The nearest free spot at or beside `origin`, searched outwards ring by ring on the layout's own
 * grid so a seated node still lines up with the generation rows around it. One grid step clears the
 * overlap test in both directions, so any spot this returns sits clear of every node `isTaken` knows
 * about. `origin` itself is the first candidate: where it is a free row below the anchor, that is
 * exactly where the node belongs, and only an occupied spot pushes the search outwards.
 *
 * The final fallback only comes up on a canvas with no free spot within twelve rings of the origin;
 * it stacks the node out to the right of the whole search area rather than dropping it on top of
 * something.
 */
export const findNearbySeat = (
	origin: { x: number; y: number },
	isTaken: (x: number, y: number) => boolean
): { x: number; y: number } => {
	for (let ring = 0; ring <= MAX_RINGS; ring += 1) {
		const candidates: Candidate[] = [];
		for (let dy = -ring; dy <= ring; dy += 1) {
			for (let dx = -ring; dx <= ring; dx += 1) {
				if (Math.max(Math.abs(dx), Math.abs(dy)) === ring) {
					candidates.push({ dx, dy, x: origin.x + dx * STEP_X, y: origin.y + dy * STEP_Y });
				}
			}
		}

		const free = candidates.sort(byReadingOrder).find((candidate: Candidate) => !isTaken(candidate.x, candidate.y));
		if (free !== undefined) {
			return { x: free.x, y: free.y };
		}
	}

	return { x: origin.x + (MAX_RINGS + 1) * STEP_X, y: origin.y };
};
